import { createSmartForm, getFormErrors } from '@hprofits/ui-smart-controls';
import type { StandardSchemaV1 } from '@tanstack/vue-form';
import type { ShallowRef } from 'vue';
import { ref, shallowRef } from 'vue';

import type { DataPayload, FormDispatcher, FormProducer } from './types';

const hasPerFieldError = (perField: Record<string, unknown> | undefined): boolean =>
  perField !== undefined && Object.values(perField).some((err) => err != null && err !== '');

type UseFormProducerOptions<TForm extends Record<string, unknown>, TSubmit extends DataPayload, TEntity> = Readonly<{
  defaults: TForm;
  schema: StandardSchemaV1<unknown, TForm>;
  /** Server entity → form values for load/sync watcher. */
  toForm?: (entity: TEntity) => TForm;
  /** Form values → submit payload. Omit when TForm = TSubmit. */
  toSubmit: (form: TForm) => TSubmit;
}>;

export const useFormProducer = <
  TForm extends Record<string, unknown>,
  TSubmit extends Record<string, unknown> = TForm,
  TEntity = never,
>(
  options: UseFormProducerOptions<TForm, TSubmit, TEntity>,
): FormProducer<TForm, TSubmit> => {
  const isPending = ref(false);

  // `createSmartForm`/`getFormErrors` (packages/ui-smart-controls) type `schema` as
  // `StandardSchemaV1<TForm, TForm>`. Our option widens the INPUT to `unknown`
  // (preprocess sentinels), so re-narrow ONCE here — runtime-safe because
  // StandardSchema `validate` always accepts `unknown`; the OUTPUT stays `TForm`.
  const schema = options.schema as StandardSchemaV1<TForm, TForm>;

  const form = createSmartForm<TForm>({
    defaultValues: options.defaults,
    validators: { onChange: schema, onSubmit: schema },
  });
  const isDirty = form.useStore((s) => s.isDirty);

  const formData = form.useStore((s) => s.values);

  const formErrors = form.useStore((s) => (s.values !== undefined ? getFormErrors(form, schema) : []));

  const submit = async (): Promise<TSubmit | false> => {
    isPending.value = true;
    try {
      const fieldResults = await form.validateAllFields('submit');
      if (fieldResults.some((r) => r != null)) {
        return false;
      }
      // validateAllFields runs only per-field validators, NOT the form-level Zod
      // schema (validators.onSubmit). Constraints that live solely in the schema
      // (e.g. PercentSchema max) would otherwise slip through to the network, so
      // run form.validate('submit') explicitly here. See useSmartFormDialog.
      const formErrorMap = (await form.validate('submit')) as Record<string, Record<string, unknown> | undefined>;
      if (Object.values(formErrorMap).some(hasPerFieldError)) {
        return false;
      }
      return options.toSubmit(formData.value);
    } finally {
      isPending.value = false;
    }
  };

  // Mirrors the form's OWN dirty baseline (TanStack defaultValues) as a Vue ref —
  // `form.options.defaultValues` is not reactive, so consumers deriving per-field dirty read
  // this instead. Updated in lockstep with every `form.reset(...)` below; never anywhere else.
  // Cast: `shallowRef` with a generic `TForm` infers the `ShallowRef | Ref` unwrap union — a
  // known Vue typing limitation, the runtime value is exactly ShallowRef<TForm>.
  const baseline = shallowRef(options.defaults) as ShallowRef<TForm>;

  const afterSubmit = async (): Promise<void> => {
    const snapshot = form.state.values;
    form.reset(snapshot);
    baseline.value = snapshot;
  };

  const reset = (next: TForm): void => {
    form.reset(next);
    baseline.value = next;
  };

  return {
    state: {
      formData,
      isDirty,
      isPending,
    },
    actions: {
      submit,
      afterSubmit,
    },
    form,
    formErrors,
    baseline,
    reset,
  };
};

export type UseFormControllerProps<
  TForm extends Record<string, unknown>,
  TSubmit extends DataPayload,
  TEntity,
> = UseFormProducerOptions<TForm, TSubmit, TEntity> &
  Readonly<{
    handleSubmit: (data: TSubmit) => Promise<void>;
  }>;

export const useFormController = <TForm extends Record<string, unknown>, TSubmit extends DataPayload, TEntity>({
  handleSubmit,
  ...options
}: UseFormControllerProps<TForm, TSubmit, TEntity>): FormDispatcher<TForm> => {
  const { actions, ...producerBase } = useFormProducer(options);
  const submit = async (): Promise<boolean> => {
    const result = await actions.submit();
    if (result !== false) {
      await handleSubmit(result);
      await actions.afterSubmit?.();
      return true;
    } else {
      return false;
    }
  };
  return {
    ...producerBase,
    actions: {
      submit,
    },
  };
};
